import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import monitor, {
  AlertData,
  DeviceOrientation,
  EnabledVitalSigns,
  FaceSessionOptions,
  HealthMonitorCodes,
  HealthMonitorSession,
  ImageValidity,
  OfflineMeasurements,
  SessionState,
  VitalSigns,
  VitalSignsResults,
} from '@biosensesignal/web-sdk';
import { InfoType, InfoData } from '../types';

const useMonitor = (
  video: MutableRefObject<HTMLVideoElement>,
  cameraId: string,
  processingTime: number,
  licenseKey: string,
  productId: string,
  startMeasuring: boolean,
) => {
  const [session, setSession] = useState<HealthMonitorSession>();
  const [sessionState, setSessionState] = useState<SessionState>();
  const [isMonitorReady, setIsMonitorReady] = useState<boolean>();
  const [enabledVitalSigns, setEnabledVitalSigns] = useState<
    EnabledVitalSigns
  >();
  const [offlineMeasurements, setOfflineMeasurements] = useState<
    OfflineMeasurements
  >();
  const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();

  const [error, setError] = useState<AlertData>({ code: -1 });
  const [warning, setWarning] = useState<AlertData>({ code: -1 });
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  const isDismissing = useRef<boolean>(false);

  const setInfoWithDismiss = useCallback(
    (info: InfoData, seconds?: number) => {
      if (!isDismissing.current) {
        setInfo(info);
        if (seconds) {
          isDismissing.current = true;
          setTimeout(() => {
            setInfo({ type: InfoType.NONE });
            isDismissing.current = false;
          }, seconds * 1000);
        }
      }
    },
    [InfoType, setInfo, info, isDismissing, isDismissing.current],
  );

  const updateVitalSigns = useCallback((vitalSigns) => {
    setVitalSigns((prev) => ({
      ...prev,
      ...vitalSigns,
    }));
  }, []);

  const onVitalSign = useCallback((vitalSign: VitalSigns) => {
    updateVitalSigns(vitalSign);
  }, []);

  const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
    console.log('onFinalResults called with:', vitalSignsResults);
    // Instead of setting to null first, directly update with new results
    setVitalSigns(vitalSignsResults.results);
  }, []);

  const onError = (errorData: AlertData) => {
    setError(errorData);
  };

  const onWarning = (warningData: AlertData) => {
    if (
      warningData.code ===
      HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
    ) {
      setVitalSigns(null);
    }
    setWarning(warningData);
  };

  const onStateChange = useCallback((state: SessionState) => {
    setSessionState(state);
    if (state === SessionState.MEASURING) {
      setVitalSigns(null);
    }
  }, []);

  const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
    console.log('Enabled vital signs received:', vitalSigns);
    setEnabledVitalSigns(vitalSigns);
  }, []);

  const onOfflineMeasurement = useCallback(
    (offlineMeasurements: OfflineMeasurements) => {
      setOfflineMeasurements(offlineMeasurements);
    },
    [],
  );

  const onActivation = useCallback((activationId: string) => {
    // the device has been activated with activationId
  }, []);

  const onImageData = useCallback((imageValidity: ImageValidity) => {
    let message: string;
    if (imageValidity != ImageValidity.VALID) {
      switch (imageValidity) {
        case ImageValidity.INVALID_DEVICE_ORIENTATION:
          message = 'Unsupported Orientation';
          break;
        case ImageValidity.TILTED_HEAD:
          message = 'Head Tilted';
          break;
        case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
          message = 'You Are Too Far';
          break;
        case ImageValidity.UNEVEN_LIGHT:
          message = 'Uneven Lighting';
          break;
        case ImageValidity.INVALID_ROI:
        default:
          message = 'Face Not Detected';
      }
      setInfo({
        type: InfoType.INSTRUCTION,
        message: message,
      });
    } else {
      setInfoWithDismiss({ type: InfoType.NONE });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await monitor.initialize({
          licenseKey,
          licenseInfo: {
            onEnabledVitalSigns,
            onOfflineMeasurement,
            onActivation,
          },
        });
        console.log(`Initialized monitor`);
        setIsMonitorReady(true);
        setError({ code: -1 });
      } catch (e) {
        console.error('Error initializing HealthMonitor', e);
        setIsMonitorReady(false);
        setError({ code: e.errorCode });
      }
    })();
  }, [licenseKey, productId]);

  useEffect(() => {
    (async () => {
      try {
        if (!isMonitorReady || !processingTime || !video.current) {
          return;
        }

        sessionState === SessionState.ACTIVE && session.terminate();

        const options: FaceSessionOptions = {
          input: video.current,
          cameraDeviceId: cameraId,
          processingTime,
          onVitalSign,
          onFinalResults,
          onError,
          onWarning,
          onStateChange,
          orientation: DeviceOrientation.PORTRAIT,
          onImageData,
          // isAnalyticsEnabled: true,
          /*******************************************************************************
           * For accurate vital signs calculation the user's parameters should be provided.
           * The following is an example of how the parameters are passed to the SDK:
           *
           * subjectDemographic: {sex: Sex.MALE, age: 35, weight: 75, height: 165}
           *
           * When measuring a new user then a new session must be created using the
           * new user's parameters
           * The parameters are used locally on the device only for the vital sign
           * calculation, and deleted when the session is terminated.
           *******************************************************************************/
        };

        const faceSession = await monitor.createFaceSession(options);
        console.log(`Session created`);
        setSession(faceSession);
        setError({ code: -1 });
      } catch (e) {
        setError({ code: e.errorCode });
        console.error('Error creating a session', e);
      }
    })();
  }, [processingTime, isMonitorReady]);

  useEffect(() => {
    if (startMeasuring) {
      if (sessionState === SessionState.ACTIVE) {
        session.start();
        setError({ code: -1 });
      }
    } else {
      sessionState === SessionState.MEASURING && session.stop();
    }
  }, [startMeasuring]);

  return {
    sessionState,
    vitalSigns: {
      // Primary vital signs
      pulseRate: {
        value: vitalSigns?.pulseRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledPulseRate,
        confidenceLevel: vitalSigns?.pulseRate?.confidenceLevel,
      },
      respirationRate: {
        value: vitalSigns?.respirationRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledRespirationRate,
        confidenceLevel: vitalSigns?.respirationRate?.confidenceLevel,
      },
      oxygenSaturation: {
        value: vitalSigns?.oxygenSaturation?.value,
        isEnabled: enabledVitalSigns?.isEnabledSpo2,
      },
      bloodPressure: {
        value: vitalSigns?.bloodPressure?.value,
        isEnabled: enabledVitalSigns?.isEnabledBloodPressure,
      },
      
      // Heart Rate Variability (HRV) metrics
      sdnn: {
        value: vitalSigns?.sdnn?.value,
        isEnabled: enabledVitalSigns?.isEnabledSdnn,
        confidenceLevel: vitalSigns?.sdnn?.confidenceLevel,
      },
      rmssd: {
        value: vitalSigns?.rmssd?.value,
        isEnabled: enabledVitalSigns?.isEnabledRmssd,
      },
      sd1: {
        value: vitalSigns?.sd1?.value,
        isEnabled: enabledVitalSigns?.isEnabledSd1,
      },
      sd2: {
        value: vitalSigns?.sd2?.value,
        isEnabled: enabledVitalSigns?.isEnabledSd2,
      },
      meanRri: {
        value: vitalSigns?.meanRri?.value,
        isEnabled: enabledVitalSigns?.isEnabledMeanRri,
        confidenceLevel: vitalSigns?.meanRri?.confidenceLevel,
      },
      rri: {
        value: vitalSigns?.rri?.value,
        isEnabled: enabledVitalSigns?.isEnabledRri,
        confidenceLevel: vitalSigns?.rri?.confidenceLevel,
      },
      prq: {
        value: vitalSigns?.prq?.value,
        isEnabled: enabledVitalSigns?.isEnabledPrq,
        confidenceLevel: vitalSigns?.prq?.confidenceLevel,
      },
      lfhf: {
        value: vitalSigns?.lfhf?.value,
        isEnabled: enabledVitalSigns?.isEnabledLfhf,
      },
      
      // Stress and wellness metrics
      stressLevel: {
        value: vitalSigns?.stressLevel?.value,
        isEnabled: enabledVitalSigns?.isEnabledStressLevel,
      },
      stressIndex: {
        value: vitalSigns?.stressIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledStressIndex,
      },
      normalizedStressIndex: {
        value: vitalSigns?.normalizedStressIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledNormalizedStressIndex,
      },
      wellnessLevel: {
        value: vitalSigns?.wellnessLevel?.value,
        isEnabled: enabledVitalSigns?.isEnabledWellnessLevel,
      },
      wellnessIndex: {
        value: vitalSigns?.wellnessIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledWellnessIndex,
      },
      
      // Autonomic nervous system metrics
      pnsIndex: {
        value: vitalSigns?.pnsIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledPnsIndex,
      },
      pnsZone: {
        value: vitalSigns?.pnsZone?.value,
        isEnabled: enabledVitalSigns?.isEnabledPnsZone,
      },
      snsIndex: {
        value: vitalSigns?.snsIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledSnsIndex,
      },
      snsZone: {
        value: vitalSigns?.snsZone?.value,
        isEnabled: enabledVitalSigns?.isEnabledSnsZone,
      },
      
      // Blood and health risk metrics
      hemoglobin: {
        value: vitalSigns?.hemoglobin?.value,
        isEnabled: enabledVitalSigns?.isEnabledHemoglobin,
      },
      hemoglobinA1c: {
        value: vitalSigns?.hemoglobinA1c?.value,
        isEnabled: enabledVitalSigns?.isEnabledHemoglobinA1c,
      },
      heartAge: {
        value: vitalSigns?.heartAge?.value,
        isEnabled: enabledVitalSigns?.isEnabledHeartAge,
      },
      
      // Health risk assessments
      highBloodPressureRisk: {
        value: vitalSigns?.highBloodPressureRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledHighBloodPressureRisk,
      },
      highHemoglobinA1CRisk: {
        value: vitalSigns?.highHemoglobinA1CRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledHighHemoglobinA1CRisk,
      },
      highFastingGlucoseRisk: {
        value: vitalSigns?.highFastingGlucoseRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledHighFastingGlucoseRisk,
      },
      highTotalCholesterolRisk: {
        value: vitalSigns?.highTotalCholesterolRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledHighTotalCholesterolRisk,
      },
      lowHemoglobinRisk: {
        value: vitalSigns?.lowHemoglobinRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledLowHemoglobinRisk,
      },
      ascvdRisk: {
        value: vitalSigns?.ascvdRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledAscvdRisk,
      },
    },
    offlineMeasurements,
    error,
    warning,
    info,
  };
};

export default useMonitor;
